import { observable, action, transaction, computed } from 'mobx'
import * as defaultConst from '../constants/default_constants'
import AwsService from '../services/aws'
import I18nState from './i18n'

export default class UploadState extends I18nState {
  constructor(...params) {
    super(...params)
    setTimeout(() => {
      if (this.auth && this.auth.getToken()) {
        this.requester.call('/uploads/uncomplete/records').then(res => {
          this.queue.replace(res.data)
        }).catch(err => { })
      }
    }, 2000)
    setInterval(this.processQueue.bind(this), (defaultConst.UPLOAD_INTERVAL_SEC * 1000))

    // warning before browser tab closing
    window.addEventListener('beforeunload', (event) => {
      if (this.currentUpload) {
        event.preventDefault()
        event.returnValue = 'Uploading in progress. Really close the window?'
      }
    })
  }

  initAws() {
    if (!this._aws) {
      this._aws = new AwsService(this.requester)
    }
  }

  uploadInterval = null
  @observable currentUpload = null
  @observable progress = 0
  @observable queue = []
  @observable uploadStopped = false
  @observable uploadTimeout = null

  @observable uploadEnc = localStorage && localStorage.getItem('uploadEncode')
    ? JSON.parse(localStorage.getItem('uploadEncode'))
    : false

  @observable uploadRes = localStorage && localStorage.getItem('uploadResume')
    ? JSON.parse(localStorage.getItem('uploadResume'))
    : true

  _updateFileInfo(key, video_id, platform, folder, filename, postfix) {
    const nfo = this.uploads.get(key)
    nfo.loading = true
    // try to get info from DB
    return this.requester.call(`/service/uploads/${video_id}/${platform}/${folder}/${filename}`).then((res) => {
      transaction(() => {
        nfo.loading = nfo.uploading = false
        if (res.data !== null) {
          nfo.data = res.data
        }
      })
    })
  }

  STATES_UNFINISHED = [
    defaultConst.STATES_UPLOAD.UPLOADING,
    defaultConst.STATES_UPLOAD.WAITING,
    defaultConst.STATES_UPLOAD.FAILED
  ]

  isFileMissing(upload) {
    const shallHaveFile = this.STATES_UNFINISHED.indexOf(upload.state) >= 0
    const inQ = this.queue.find(i => i.id === upload.id)
    return shallHaveFile && ((inQ && !upload.file) || !inQ)
  }

  _getAwsFileName(platform, folder, slug, filename, postfix) {
    return `${platform}/${folder}/${slug}_${filename}.${postfix}`
  }

  @action getAwsFileInfo(video_id, slug, platform, folder, filename, postfix) {
    const awsfilename = this._getAwsFileName(platform, folder, slug, filename, postfix)
    const nfo = {
      content: null,
      data: null,
      error: null,
      filename: filename,
      folder: folder,
      key: awsfilename,
      loading: true,
      platform: platform,
      postfix: postfix,
      progress: 0,
      removing: false,
      updated: '',
      uploading: false,
      video_id: video_id
    }
    this.uploads.set(awsfilename, nfo)
    this._updateFileInfo(awsfilename, video_id, platform, folder, filename, postfix)
      .catch(this.onRequesterError.bind(this))
  }

  _addFileAttrs(upload) {
    const file = upload.file
    if (file) {
      Object.assign(upload, {
        final_length: file.size,
        original_filename: file.name,
        platform: 'flyprivate'
      })
    }
  }

  _doSave2DB(upload) {
    const op = upload.id ? 'put' : 'post'
    const url = upload.id ? `/uploads/${upload.id}` : '/uploads'
    return this.requester.call(url, op, upload)
      .then(res => {
        Object.assign(upload, res.data)
        return upload
      })
      .then(this._updateTableState.bind(this))
  }

  processQueue() {
    if (!this.uploadStopped && this.queue.length > 0 && this.currentUpload === null) {
      let current = this.queue.find((i) => {
        return i.file !== undefined && i.state === defaultConst.STATES_UPLOAD.WAITING
      })
      // use other in case there are no more waiting
      const NOTWAITING = [
        defaultConst.STATES_UPLOAD.UPLOADING,
        defaultConst.STATES_UPLOAD.CANCELLED,
        defaultConst.STATES_UPLOAD.FAILED
      ]
      current = current || this.queue.find((i) => {
        return i.file !== undefined && NOTWAITING.indexOf(i.state) >= 0
      })
      if (current === undefined || current === null) {
        return
      }
      this.sameProgressCount = 0
      this.currentUpload = current
      current.state = defaultConst.STATES_UPLOAD.UPLOADING
      return this._doSave2DB(current)
        .then(saved => {
          this.addMessage('Upload started', 'info')
          return this._aws.upload(saved.s3_path, current.file,
            saved, this.updateProgress.bind(this))
        })
        .catch(e => { console.log(e) }) // User aborted the upload
        .then(this.onUploaded.bind(this))
        .catch(err => {
          if (this.uploadStopped) {
            this.currentUpload = null
            return true
          }
          this.stopQueueAction('Unexpected error during upload')

          if (err === 'Unexpected end of input') {
            this.currentUpload.state = defaultConst.STATES_UPLOAD.FAILED
          }
          if (this.currentUpload && this.currentUpload.state === defaultConst.STATES_UPLOAD.FAILED) {
            this.failing = false
            this.sameProgressCount = 0
            this._doSave2DB(this.currentUpload).catch(err => { console.log(err) })
          }
          this.currentUpload = null
        })
    }
  }

  _updateTableState(saved) {
    // if we are on view that upload belongs to, update table data
    try {
      const p = this.router.params
      if (p && p.entityname === 'films' && p.id && p.id.toString() === saved.video_id.toString()) {
        const found = this.cv.uploads.find(i => i.id === saved.id)
        found && Object.assign(found, saved)
      }
    } catch (e) {
      console.error(e)
    }
    return saved
  }

  @action onUploaded(upload) {
    upload.state = 'uploaded'
    return this._doSave2DB(upload)
      .then(saved => {
        console.log('Uploaded file:', this.currentUpload)
        if (this.uploadEnc && defaultConst.VIDEO_TYPES.indexOf(this.currentUpload.file_type) >= 0) {
          console.log('Autorun for encoding started')
          this.encodeVideo(this.currentUpload, this.currentUpload.file_type === 'feature' ? 'all-encoding' : 'encoding')
        }
        this.currentUpload = null
        this.addMessage('Successfully uploaded', 'success')
      }).catch(_ => {
        this.stopQueueAction('Unexpected end of input')
      })
  }

  MAX_SAME_COUNT = 10

  @action updateProgress(progress) {
    this.progress = (progress * 100).toFixed(2)
    // detection of staked upload
    if (this.lastProgress === progress) {
      this.sameProgressCount += 1
      if (this.sameProgressCount > this.MAX_SAME_COUNT && !this.failing) {
        this._failUpload()
      }
    } else {
      this.sameProgressCount = 0
    }
    this.lastProgress = progress
  }

  _failUpload() {
    this.failing = true
    this.currentUpload.state = defaultConst.STATES_UPLOAD.FAILED
    this._aws.cancel()
    this.stopQueueAction(`File ${this.currentUpload.original_filename} upload seems stucked`)
  }

  @action changeFile(upload, file) {
    upload.file = file
    this.saveUpload(upload)
  }

  inQueue(upload) {
    return this.queue.find(i => i.id === upload.id) !== undefined
  }

  showFileBrowse(record) {
    const enqueued = this.inQueue({ id: record.get('id') })
    const creating = !record.has('id')
    const showFile = creating ||
      (defaultConst.STATES_UNFINISHED.indexOf(record.get('state')) >= 0 && (
        !enqueued || !enqueued.file)
      )
    return showFile
  }

  saveUpload(item, existing = {}) {
    if (item && item.record) {
      item = Object.fromEntries(item.record)
    } else if (typeof item !== 'object') {
      item = Object.fromEntries(item)
    }
    const adding = !item.id
    Object.assign(existing, item)
    Object.assign(existing, { updated: new Date() }) // set updated flag !!!!!!!!!!!!!!!

    if (item.loadedfromcopy) {
      this._doSave2DB(existing)
        .then(action(saved => this.cv.uploads.push(existing)))
        .catch(this.onRequesterError.bind(this))

      return
    }

    if (adding) {
      Object.assign(existing, {
        slug: existing.slug || this.cv.record.get('slug'), // add slug to prevent another backend DB query
        state: defaultConst.STATES_UPLOAD.WAITING,
        updated: new Date() // set updated flag !!!!!!!!!!!!!!!
      })
    }
    this._addFileAttrs(existing)

    const enqueued = existing.id && this.inQueue({ id: existing.id })
    if (existing.file && !enqueued) {
      Object.assign(existing, { state: defaultConst.STATES_UPLOAD.WAITING })
    }
    return this._doSave2DB(existing)
      .then(action(saved => {
        existing.file && this._enque(existing)
        this.cv.uploads = this.cv.uploads || []
        adding && this.cv.uploads.push(existing)
      }))
      .catch(this.onRequesterError.bind(this))
  }

  _enque(upload) {
    const existing = this.queue.find(i => i.id === upload.id)
    if (existing) {
      Object.assign(existing, upload) // update
    } else {
      this.queuePush(upload)
    }
  }

  isCurrUpload(upload) {
    return this.currentUpload !== null && upload.id === this.currentUpload.id
  }

  checkState() {
    if (this.cv.uploads !== undefined && this.cv.uploads.length > 0) {
      const isEncodig = (i) => i.state === 'encoding' || i.state === 'encoding-stream' || i.state[0] === '{'
      const encoding = this.cv.uploads.filter(isEncodig)
      encoding.map((i) => {
        this.requester.call(`/uploads/status/${i.id}`)
          .then((res) => {
            if (res.data && res.data.state !== 'encoding' && res.data.state !== 'encoding-stream') {
              i.state = res.data.state
            }
          })
          .catch(this.onRequesterError.bind(this))
      })
      if (encoding.length > 0 && !this.statusCheckInterval) {
        this.statusCheckInterval = setInterval(this.checkState.bind(this), (defaultConst.UPLOAD_INTERVAL_SEC * 1000))
      } else if (encoding.length === 0 && this.statusCheckInterval) {
        clearInterval(this.statusCheckInterval)
      }
    }
  }

  @action cancelUpload(upload) {
    delete upload.file
    if (this.isCurrUpload(upload)) {
      this._aws.cancel()
    }
    return this._doSave2DB(Object.assign(upload, {
      state: defaultConst.STATES_UPLOAD.CANCELLED
    }))
      .catch(this.onRequesterError.bind(this))
  }

  @action removeUpload(upload) {
    const p = this.isCurrUpload(upload)
      ? this._aws.cancel()
      : this._aws.remove(upload.s3_path)
    if (this.isCurrUpload(upload)) {
      this.currentUpload = null // NEEEDED!!!
    }
    p.then((status) => {
      return this.requester.call(`/uploads/${upload.id}`, 'delete')
    })
      .then((res) => {
        this.cv.uploads.remove(upload)
        this.queue.remove(this.queue.find(i => i.id === upload.id))
        this.addMessage('Successfully removed', 'info')
      })
      .catch(this.onRequesterError.bind(this))
  }

  @action encodeVideo(upload, enctype = '') {
    if (!upload.id) {
      upload = JSON.parse(JSON.stringify(upload))
    }
    const url = `/uploads/encode/${upload.id}?enctype=${enctype}`
    return this.requester
      .call(url, 'put')
      .catch(err => {
        console.log(err.message)
        // this.addMessage(`Missing file. ${err.message}.`, 'error')
      })
      .then((res) => {
        upload.state = (enctype === 'stream-encoding' ? 'encoding-stream' : 'encoding')
        this.checkState()
      })
      .catch(this.onRequesterError.bind(this))
  }

  @action resumeQueueAction = () => {
    this.uploadStopped = false
    clearTimeout(this.uploadTimeout)
    this.addMessage('Upload queue resumed', 'info')
  }

  @action stopQueueAction = (error = null) => {
    this.currentUpload = null
    if (this._aws && this._aws._evaporate) {
      this.uploadStopped = true
      this._aws._evaporate.cancel()
        .catch(e => { console.log(e) })
        .then(_ => {
          delete this._aws._evaporate
        })
        .catch(e => { console.log(e) })
        .then(_ => {
          this.queue.map(i => {
            i.state = i.state === defaultConst.STATES_UPLOAD.UPLOADING
              ? defaultConst.STATES_UPLOAD.WAITING
              : i.state
          })
          this.addMessage('Upload queue stopped', 'info')
        })
        .catch(this.onRequesterError(this))
    } else if (error) {
      this.uploadStopped = error.message || error
      this.addMessage('Upload queue error. ' + this.uploadStopped, 'error')
      if (this.uploadRes) {
        this.uploadTimeout = setTimeout(_ => this.resumeQueueAction(),
          (defaultConst.UPLOAD_TIMEOUT_MIN * 1000 * 60))
      }
      // this.onRequesterError(error)
    } else {
      this.uploadStopped = true
      this.activeUploads > 0
        ? this.addMessage('Upload queue stopped', 'info')
        : this.addMessage('There is nothing to stop', 'warning')
    }
  }

  @action updateUploadEnc = () => {
    this.uploadEnc = !this.uploadEnc
    localStorage.setItem('uploadEncode', this.uploadEnc)
  }

  @action updateUploadRes = () => {
    this.uploadRes = !this.uploadRes
    localStorage.setItem('uploadResume', this.uploadRes)
    if (this.uploadRes && this.uploadStopped) {
      this.uploadTimeout = setTimeout(_ => this.resumeQueueAction(),
        (defaultConst.UPLOAD_TIMEOUT_MIN * 1000 * 60))
    } else if (!this.uploadRes) {
      clearTimeout(this.uploadTimeout)
    }
  }
}
